import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Badge, Select, Spinner, Empty, fmtNum } from "@/components/ui/Primitives";
import Pagination from "@/components/ui/Pagination";
import { api, formatErr, fmtDate } from "@/lib/api";
import { Plus, X, Trash2, Eye, Printer, ArrowLeft, Package, QrCode, ChevronDown, Search } from "lucide-react";

const CATS = [
  { k: "cx1", label: "CX1" }, { k: "cx2", label: "CX2" },
  { k: "cx3", label: "CX3" }, { k: "cx3g", label: "CX3G" }, { k: "cx4", label: "CX4" },
];

export function PalletsList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [err, setErr] = useState("");
  const [renumPreview, setRenumPreview] = useState(null); // { plano: [...], applied: bool }
  const [renumBusy, setRenumBusy] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setQDebounced(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => { setPage(1); }, [status, qDebounced, pageSize]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (status) params.status = status;
      if (qDebounced) params.q = qDebounced;
      const { data } = await api.get("/pallets", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (e) { setErr(formatErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, qDebounced, page, pageSize]);

  const onDelete = async (id) => {
    if (!window.confirm("Excluir pallet?")) return;
    try {
      await api.delete(`/pallets/${id}`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "";
      if (/expedid/i.test(msg)) {
        const motivo = window.prompt(
          "Este pallet aparece como EXPEDIDO no sistema.\n\n" +
          "Se ele é FANTASMA (mercadoria nunca existiu), forçar a exclusão registra o motivo em auditoria.\n\n" +
          "Digite o motivo:",
          "fantasma"
        );
        if (!motivo) return;
        try { await api.delete(`/pallets/${id}`, { params: { force: true, motivo } }); await load(); }
        catch (e2) { alert("Falhou: " + formatErr(e2)); }
      } else {
        alert(formatErr(e));
      }
    }
  };

  return (
    <Layout
      title="Pallets"
      subtitle="Rastreabilidade de caixas com QR Code"
      actions={
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              if (renumBusy) return;
              setRenumBusy(true);
              try {
                const dry = await api.post("/pallets/renumber-duplicates?dry_run=true");
                if ((dry.data.total_renumerar || 0) === 0) {
                  alert("Nenhum pallet duplicado encontrado.");
                  return;
                }
                setRenumPreview({ plano: dry.data.plano || [], applied: false, grupos: dry.data.grupos });
              } catch (e) { alert(formatErr(e)); }
              finally { setRenumBusy(false); }
            }}
            data-testid="renumber-duplicates-pallets"
            title="Atribui novos números únicos aos pallets duplicados (sem apagar nada)"
          >
            Renumerar duplicados
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              try {
                const dry = await api.post("/pallets/dedupe?dry_run=true");
                if (dry.data.a_remover === 0) { alert("Sem duplicatas encontradas."); return; }
                if (!window.confirm(`⚠ ATENÇÃO: Isso vai APAGAR ${dry.data.a_remover} pallets duplicados (mantendo o mais antigo de cada grupo).\n\nSe os pallets duplicados são FÍSICOS DIFERENTES, use "Renumerar duplicados" no lugar.\n\nContinuar com a remoção?`)) return;
                const r = await api.post("/pallets/dedupe?dry_run=false");
                alert(`✔ ${r.data.removidos} pallets duplicados removidos.`);
                await load();
              } catch (e) { alert(formatErr(e)); }
            }}
            data-testid="dedupe-pallets"
            title="Detectar e remover pallets duplicados (apenas admin)"
          >
            Remover duplicados
          </Button>
          <Link to="/pallets/new"><Button data-testid="new-pallet-button"><Plus size={16} /> Novo pallet</Button></Link>
        </div>
      }
    >
      <Card className="p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-7">
            <div className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Buscar</div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" />
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Número (P-0001) ou cliente..."
                className="w-full rounded-md bg-stone-50 border border-stone-300 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                data-testid="pallet-search-input"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="pallet-status-filter">
              <option value="">Todos</option>
              <option value="aberto">Aberto</option>
              <option value="fechado">Fechado</option>
              <option value="expedido">Expedido</option>
            </Select>
          </div>
          <div className="md:col-span-2 text-right">
            <span className="text-xs text-ink-500">{total} pallet(s)</span>
          </div>
        </div>
      </Card>
      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}
      {loading ? <div className="flex justify-center py-20"><Spinner size={28} /></div> : (
        <Card className="overflow-hidden">
          {items.length === 0 ? (
            <Empty title="Sem pallets" hint="Crie um pallet para começar a rastrear." />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-tertiary">
                <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                  <th className="px-4 py-3">Número</th>
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3 text-right">Caixas</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Criado</th>
                  <th className="px-4 py-3 w-1">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b border-tertiary last:border-0 hover:bg-stone-50" data-testid={`pallet-row-${p.id}`}>
                    <td className="px-4 py-3 font-mono font-semibold">
                      {p.numero}
                      {p.granel && <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] rounded bg-amber-100 text-amber-800 font-bold uppercase">granel</span>}
                    </td>
                    <td className="px-4 py-3 text-ink-700">{p.cliente_nome || "—"}</td>
                    <td className="px-4 py-3 font-mono text-right">
                      {fmtNum(p.total_caixas, p.granel ? 2 : 0)}
                      {p.granel && <span className="text-[10px] text-ink-500 ml-1">kg</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={p.status === "aberto" ? "warn" : p.status === "fechado" ? "info" : "success"}>{p.status}</Badge>
                    </td>
                    <td className="px-4 py-3 font-mono text-ink-500">{fmtDate(p.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Link to={`/pallets/${p.id}`}><button className="p-1.5 rounded-md hover:bg-emerald-100 text-brand-500" title="Abrir"><Eye size={16} /></button></Link>
                      {p.status !== "expedido" && (
                        <button onClick={() => onDelete(p.id)} className="p-1.5 rounded-md hover:bg-red-100 text-danger" title="Excluir"><Trash2 size={16} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={setPageSize}
            testidPrefix="pallets-pagination"
          />
        </Card>
      )}

      {/* Modal: Renumerar Duplicados */}
      {renumPreview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !renumBusy && setRenumPreview(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="renumber-modal">
            <div className="px-6 py-4 border-b border-tertiary flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-bold text-ink-900">
                  {renumPreview.applied ? "✓ Pallets renumerados" : "Renumerar Duplicados — Prévia"}
                </h3>
                <p className="text-xs text-ink-500 mt-0.5">
                  {renumPreview.applied
                    ? `${renumPreview.plano.length} pallet(s) receberam novos números. Reimprima as etiquetas dos pallets abaixo.`
                    : `${renumPreview.grupos} grupo(s) duplicados · ${renumPreview.plano.length} pallet(s) serão renumerados. Nada será apagado.`}
                </p>
              </div>
              <button
                onClick={() => !renumBusy && setRenumPreview(null)}
                className="p-1.5 rounded-md hover:bg-stone-100 text-ink-500"
                data-testid="renumber-modal-close"
                disabled={renumBusy}
              >
                <X size={18} />
              </button>
            </div>

            <div className="overflow-auto flex-1">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-tertiary sticky top-0">
                  <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                    <th className="px-4 py-3">Nº Antigo</th>
                    <th className="px-4 py-3">Nº Novo</th>
                    <th className="px-4 py-3 text-right">Caixas</th>
                    <th className="px-4 py-3">NF / Fornecedor</th>
                    <th className="px-4 py-3">Cliente</th>
                    <th className="px-4 py-3">Tipo</th>
                    <th className="px-4 py-3">Criado</th>
                    <th className="px-4 py-3 w-1">Etiqueta</th>
                  </tr>
                </thead>
                <tbody>
                  {renumPreview.plano.map((p, idx) => (
                    <tr key={p.pallet_id} className="border-b border-tertiary last:border-0 hover:bg-stone-50" data-testid={`renumber-row-${idx}`}>
                      <td className="px-4 py-3 font-mono text-danger line-through">{p.numero_antigo}</td>
                      <td className="px-4 py-3 font-mono font-bold text-brand-500">{p.numero_novo}</td>
                      <td className="px-4 py-3 font-mono text-right">{fmtNum(p.total_caixas, 0)}</td>
                      <td className="px-4 py-3 text-xs">
                        {p.nota_numero ? <span className="font-semibold">NF #{p.nota_numero}</span> : "—"}
                        {p.fornecedor && <div className="text-ink-500">{p.fornecedor}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-700">{p.cliente_nome || "—"}</td>
                      <td className="px-4 py-3 text-xs">
                        <Badge tone={p.tipo === "terceiros" ? "warn" : "info"}>{p.tipo}</Badge>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-ink-500">{fmtDate(p.created_at)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {renumPreview.applied ? (
                          <a
                            href={`/pallets/${p.pallet_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-brand-500 text-white text-xs hover:bg-brand-600"
                            data-testid={`renumber-print-${idx}`}
                            title="Abrir pallet em nova aba para imprimir etiqueta"
                          >
                            <Printer size={12} /> Imprimir
                          </a>
                        ) : (
                          <span className="text-ink-300 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-6 py-4 border-t border-tertiary bg-stone-50 flex items-center justify-between gap-3">
              <div className="text-xs text-ink-500">
                {renumPreview.applied
                  ? "Dica: abra cada pallet acima em nova aba e clique em \"Imprimir etiqueta\" para gerar a nova QR."
                  : "Ao confirmar, apenas o campo \"número\" dos pallets duplicados será alterado. Nenhuma caixa, NF ou saída será afetada."}
              </div>
              <div className="flex gap-2">
                {!renumPreview.applied ? (
                  <>
                    <Button variant="secondary" onClick={() => setRenumPreview(null)} disabled={renumBusy} data-testid="renumber-cancel">Cancelar</Button>
                    <Button
                      onClick={async () => {
                        setRenumBusy(true);
                        try {
                          const r = await api.post("/pallets/renumber-duplicates?dry_run=false");
                          setRenumPreview({ plano: r.data.plano || [], applied: true, grupos: r.data.grupos });
                          await load();
                        } catch (e) { alert(formatErr(e)); }
                        finally { setRenumBusy(false); }
                      }}
                      disabled={renumBusy}
                      data-testid="renumber-confirm"
                    >
                      {renumBusy ? "Renumerando..." : `Confirmar renumeração (${renumPreview.plano.length})`}
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => setRenumPreview(null)} data-testid="renumber-done">Fechar</Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export function PalletForm() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [clientes, setClientes] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    cliente_id: "", observacoes: "",
    itens: [], // [{invoice_id, nota_numero, fornecedor, available, cx1:{qtd,sac}, ...}]
  });

  useEffect(() => {
    Promise.all([api.get("/clientes"), api.get("/stock/available"), isEdit ? api.get(`/pallets/${id}`) : Promise.resolve(null)])
      .then(([cs, av, pl]) => {
        setClientes(cs.data || []);
        setAvailable(av.data || []);
        if (pl?.data) {
          const grouped = {};
          for (const it of pl.data.itens || []) {
            const k = it.invoice_id;
            if (!grouped[k]) {
              const m = (av.data || []).find((a) => a.invoice_id === k);
              grouped[k] = { invoice_id: k, nota_numero: it.nota_numero, fornecedor: it.fornecedor, available: m?.available || {cx1:0,cx2:0,cx3:0,cx3g:0,cx4:0},
                cx1: { qtd: "", sac: "Genérica" }, cx2: { qtd: "", sac: "Genérica" },
                cx3: { qtd: "", sac: "Genérica" }, cx3g: { qtd: "", sac: "Genérica" }, cx4: { qtd: "", sac: "Genérica" },
              };
            }
            grouped[k][it.categoria] = { qtd: it.quantidade ?? "", sac: it.sacaria_tipo || "Genérica" };
          }
          setForm({
            cliente_id: pl.data.cliente_id || "",
            observacoes: pl.data.observacoes || "",
            itens: Object.values(grouped),
          });
        }
      }).catch((e) => setErr(formatErr(e))).finally(() => setLoading(false));
  }, [id, isEdit]);

  const addInvoice = (av) => {
    if (form.itens.find((i) => i.invoice_id === av.invoice_id)) return;
    setForm({ ...form, itens: [...form.itens, {
      invoice_id: av.invoice_id, nota_numero: av.nota_numero, fornecedor: av.fornecedor, available: av.available,
      cx1: { qtd: "", sac: "Genérica" }, cx2: { qtd: "", sac: "Genérica" },
      cx3: { qtd: "", sac: "Genérica" }, cx3g: { qtd: "", sac: "Genérica" }, cx4: { qtd: "", sac: "Genérica" },
    }] });
  };
  const removeInvoice = (invId) => setForm({ ...form, itens: form.itens.filter((i) => i.invoice_id !== invId) });
  const setItem = (invId, cat, field, val) => setForm({
    ...form,
    itens: form.itens.map((i) => i.invoice_id === invId ? { ...i, [cat]: { ...i[cat], [field]: val } } : i),
  });

  const calc = useMemo(() => {
    let total = 0; const overflows = [];
    for (const it of form.itens) for (const c of CATS) {
      const q = Number(it[c.k].qtd) || 0;
      total += q;
      if (q > (it.available?.[c.k] || 0)) overflows.push(`NF #${it.nota_numero} ${c.label}: ${q} > ${it.available?.[c.k] || 0}`);
    }
    return { total, overflows };
  }, [form]);

  const save = async () => {
    if (form.itens.length === 0) { setErr("Adicione ao menos uma NF."); return; }
    setBusy(true); setErr("");
    try {
      const itens = [];
      for (const it of form.itens) for (const c of CATS) {
        const q = Number(it[c.k].qtd) || 0;
        if (q > 0) itens.push({ invoice_id: it.invoice_id, categoria: c.k, quantidade: q, sacaria_tipo: it[c.k].sac });
      }
      const payload = { cliente_id: form.cliente_id, observacoes: form.observacoes, itens };
      const { data } = isEdit ? await api.put(`/pallets/${id}`, payload) : await api.post("/pallets", payload);
      nav(`/pallets/${data.id}`);
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <Layout title="Pallet"><div className="flex justify-center py-20"><Spinner size={28} /></div></Layout>;

  return (
    <Layout
      title={isEdit ? "Editar pallet" : "Novo pallet"}
      subtitle="Vincule caixas a um pallet rastreável"
      actions={<Button variant="secondary" onClick={() => nav("/pallets")}><ArrowLeft size={16} /> Voltar</Button>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4">Dados</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select label="Cliente (opcional)" value={form.cliente_id} onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}>
                <option value="">Sem cliente definido</option>
                {clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </Select>
              <input className="hidden" />
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-3">NFs disponíveis</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-72 overflow-y-auto">
              {available.length === 0 && <div className="col-span-2 text-center py-6 text-sm text-ink-500">Sem NFs disponíveis no estoque.</div>}
              {available.map((av) => {
                const used = !!form.itens.find((i) => i.invoice_id === av.invoice_id);
                return (
                  <button key={av.invoice_id} onClick={() => addInvoice(av)} disabled={used} type="button" data-testid={`pallet-add-nf-${av.invoice_id}`}
                    className={`text-left p-3 rounded-md border transition-colors ${used ? "border-emerald-300 bg-emerald-50 opacity-60" : "border-tertiary hover:border-brand-500 hover:bg-brand-50"}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">NF #{av.nota_numero}</span>
                      <Badge tone="neutral">{fmtNum(av.total, 0)} cx</Badge>
                    </div>
                    <div className="text-xs text-ink-500 mt-0.5 truncate">{av.fornecedor}</div>
                  </button>
                );
              })}
            </div>
          </Card>

          {form.itens.length > 0 && (
            <Card className="p-6">
              <h3 className="font-heading text-lg font-bold text-ink-900 mb-3">Itens do pallet</h3>
              <div className="space-y-3">
                {form.itens.map((it) => (
                  <div key={it.invoice_id} className="border border-tertiary rounded-md">
                    <div className="flex items-center justify-between px-4 py-2 bg-stone-50">
                      <div className="font-semibold">NF #{it.nota_numero} <span className="text-xs text-ink-500 ml-2">{it.fornecedor}</span></div>
                      <button type="button" onClick={() => removeInvoice(it.invoice_id)} className="p-1 rounded-md hover:bg-red-100 text-danger"><Trash2 size={14} /></button>
                    </div>
                    <div className="p-3 space-y-2">
                      {CATS.map((c) => {
                        const q = Number(it[c.k].qtd) || 0;
                        const disp = it.available[c.k] || 0;
                        const over = q > disp;
                        return (
                          <div key={c.k} className="grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-3 text-xs uppercase tracking-wider text-ink-500 font-bold">{c.label} <span className="text-ink-300">/{fmtNum(disp, 0)}</span></div>
                            <input type="number" min="0" value={it[c.k].qtd} onChange={(e) => setItem(it.invoice_id, c.k, "qtd", e.target.value)}
                              data-testid={`pallet-item-${it.invoice_id}-${c.k}`}
                              className={`col-span-4 rounded-md bg-white border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${over ? "border-danger focus:ring-red-500" : "border-[#D6D2CC] focus:ring-brand-500/20"}`} />
                            <select value={it[c.k].sac} onChange={(e) => setItem(it.invoice_id, c.k, "sac", e.target.value)}
                              className="col-span-5 rounded-md bg-white border border-[#D6D2CC] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20">
                              <option value="Genérica">Sacaria Genérica</option>
                              <option value="Serenata">Sacaria Serenata</option>
                              <option value="Usada">Sacaria Usada</option>
                              <option value="Roxa">Sacaria Roxa</option>
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div>
          <Card className="p-6 sticky top-20">
            <h3 className="font-heading font-bold text-ink-900 mb-3">Resumo</h3>
            <div className="text-sm space-y-2">
              <div className="flex justify-between"><span className="text-ink-500">Total de caixas</span><span className="font-mono text-2xl font-bold text-brand-500">{fmtNum(calc.total, 0)}</span></div>
            </div>
            {calc.overflows.length > 0 && (
              <div className="mt-3 text-xs text-danger bg-red-50 border border-red-200 rounded-md px-3 py-2 space-y-0.5">
                {calc.overflows.map((o, i) => <div key={i}>• {o}</div>)}
              </div>
            )}
            {err && <div className="mt-3 text-sm text-danger bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
            <Button onClick={save} size="lg" className="w-full mt-4" disabled={busy || calc.overflows.length > 0} data-testid="pallet-save-button">
              {busy ? <Spinner size={16} /> : "Salvar"}
            </Button>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

export function PalletDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [pl, setPl] = useState(null);
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [p, q] = await Promise.all([api.get(`/pallets/${id}`), api.get(`/pallets/${id}/qr`)]);
      setPl(p.data); setQr(q.data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (status) => {
    try { const { data } = await api.post(`/pallets/${id}/status?status=${status}`); setPl(data); }
    catch (e) { alert(formatErr(e)); }
  };

  if (loading) return <Layout title="Pallet"><div className="flex justify-center py-20"><Spinner size={28} /></div></Layout>;
  if (!pl) return <Layout title="Pallet"><div className="text-danger">{err || "Não encontrado"}</div></Layout>;

  // Agrupa itens por NF
  const byInv = {};
  for (const it of pl.itens || []) {
    if (!byInv[it.invoice_id]) byInv[it.invoice_id] = { nota_numero: it.nota_numero, fornecedor: it.fornecedor, items: [] };
    byInv[it.invoice_id].items.push(it);
  }

  return (
    <Layout
      title={`Pallet ${pl.numero}`}
      subtitle={pl.cliente_nome || "Sem cliente definido"}
      actions={
        <div className="flex gap-2 print:hidden">
          <Button variant="secondary" onClick={() => nav("/pallets")}><ArrowLeft size={16} /> Voltar</Button>
          <Button variant="secondary" onClick={() => window.print()} data-testid="pallet-print-button"><Printer size={16} /> Imprimir etiqueta</Button>
          {pl.status === "aberto" && <Button onClick={() => setStatus("fechado")} data-testid="pallet-close-button">Fechar pallet</Button>}
          {pl.status === "fechado" && <Button onClick={() => setStatus("expedido")} data-testid="pallet-ship-button">Marcar expedido</Button>}
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 print:block">
        <Card className="lg:col-span-2 p-6 print:p-0 print:border-0 print:shadow-none">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold text-ink-900">Conteúdo ({fmtNum(pl.total_caixas, 0)} caixas)</h3>
            <Badge tone={pl.status === "aberto" ? "warn" : pl.status === "fechado" ? "info" : "success"}>{pl.status}</Badge>
          </div>
          <div className="space-y-3">
            {Object.entries(byInv).map(([inv_id, g]) => (
              <div key={inv_id} className="border border-tertiary rounded-md">
                <div className="px-4 py-2 bg-stone-50 border-b border-tertiary">
                  <span className="font-semibold">NF #{g.nota_numero}</span>
                  <span className="ml-2 text-xs text-ink-500">{g.fornecedor}</span>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="text-xs uppercase tracking-wider text-ink-500"><th className="px-4 py-2 text-left">Categoria</th><th className="px-4 py-2 text-left">Sacaria</th><th className="px-4 py-2 text-right">Qtd</th></tr></thead>
                  <tbody>
                    {g.items.map((it, i) => (
                      <tr key={i} className="border-t border-tertiary"><td className="px-4 py-2 uppercase font-mono">{it.categoria}</td><td className="px-4 py-2">{it.sacaria_tipo}</td><td className="px-4 py-2 font-mono text-right font-semibold">{fmtNum(it.quantidade, 0)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </Card>

        {/* Etiqueta com QR */}
        <Card className="p-6 print:border print:shadow-none">
          <h3 className="font-heading font-bold text-ink-900 mb-3 flex items-center gap-2"><QrCode size={16} /> Etiqueta</h3>
          <div className="border-2 border-stone-900 rounded-md p-4 text-center bg-white" data-testid="pallet-etiqueta">
            <div className="font-heading font-extrabold text-2xl tracking-wider">CEBOLAS SERENATA</div>
            <div className="text-xs uppercase tracking-widest text-ink-500 mt-1">PALLET</div>
            <div className="font-mono font-extrabold text-3xl mt-3 mb-3">{pl.numero}</div>
            {qr?.data_url && <img src={qr.data_url} alt="QR Code" className="mx-auto" style={{ width: 200, height: 200 }} />}
            <div className="font-mono text-[10px] text-ink-500 mt-2 break-all">{qr?.payload}</div>
            <div className="text-xs text-ink-700 mt-3">{fmtNum(pl.total_caixas, 0)} caixas · {fmtDate(pl.created_at)}</div>
            {pl.cliente_nome && <div className="text-xs font-semibold text-ink-900 mt-1">→ {pl.cliente_nome}</div>}
          </div>
          {pl.observacoes && (
            <div className="mt-4 text-xs text-ink-700 print:hidden"><span className="text-ink-500 font-semibold">Obs:</span> {pl.observacoes}</div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
